import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AllReportsModal } from "./AllReportsModal";
import { FileText, Clock, X, Minus, ChevronDown } from "lucide-react";
import { listRDOs } from "@/services/rdoStorageSupabase";
import type { RDO } from "@/services/rdoStorageService";

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  approved: { label: "Aprovado", variant: "default" },
  signed: { label: "Assinado", variant: "default" },
  in_review: { label: "Em Revisão", variant: "secondary" },
  draft: { label: "Rascunho", variant: "outline" },
};

const formatDate = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

interface RecentReportsProps {
  obraId?: string;
  onClose?: () => void;
  onMinimize?: () => void;
  isMinimized?: boolean;
}

export function RecentReports({ obraId, onClose, onMinimize, isMinimized }: RecentReportsProps) {
  const navigate = useNavigate();
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
  const [rdos, setRdos] = useState<RDO[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    if (!obraId) { setRdos([]); setLoading(false); return; }
    setLoading(true);
    listRDOs(obraId)
      .then((list) => { if (active) setRdos(list.slice(0, 5)); })
      .catch((e) => console.error("RecentReports:", e))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [obraId]);

  const openRdo = (rdo: RDO) => {
    navigate(`/rdo?obra=${rdo.obraId}&date=${rdo.date}`);
  };

  return (
    <>
      <Card className="shadow-card transition-shadow">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center justify-between w-full">
            <CardTitle className="text-lg font-semibold text-foreground">
              Últimos Relatórios
            </CardTitle>
            <div className="flex items-center gap-1">
              <button
                className="text-sm text-primary hover:text-primary/80 underline font-medium"
                onClick={() => setIsReportsModalOpen(true)}
              >
                Ver todos
              </button>
              {onMinimize && (
                <button
                  onClick={onMinimize}
                  className="p-1 rounded hover:bg-muted transition-colors"
                  title={isMinimized ? "Maximizar" : "Minimizar"}
                >
                  {isMinimized ? <ChevronDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1 rounded hover:bg-muted transition-colors"
                  title="Fechar"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        {!isMinimized && (
          <CardContent className="space-y-4">
            {loading ? (
              <div className="space-y-2">
                <div className="h-14 rounded-lg bg-muted animate-pulse" />
                <div className="h-14 rounded-lg bg-muted animate-pulse" />
              </div>
            ) : rdos.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Nenhum relatório preenchido nesta obra ainda.
              </div>
            ) : (
              rdos.map((rdo) => {
                const statusInfo = statusConfig[rdo.status ?? "draft"] ?? statusConfig.draft;
                return (
                  <div
                    key={rdo.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => openRdo(rdo)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <FileText className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">RDO {formatDate(rdo.date)}</h4>
                        {rdo.approvedBy && (
                          <p className="text-sm text-muted-foreground">aprovado por {rdo.approvedBy}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>{formatDate(rdo.date)}</span>
                        </div>
                      </div>
                      <Badge variant={statusInfo.variant} className="text-xs">
                        {statusInfo.label}
                      </Badge>
                    </div>
                  </div>
                );
              })
            )}

            <AllReportsModal
              open={isReportsModalOpen}
              onOpenChange={setIsReportsModalOpen}
              obraId={obraId}
            />
          </CardContent>
        )}
      </Card>
    </>
  );
}
